
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { recipeService } from '@/services/recipe';
import { StructuredIngredient, Recipe } from '@/types/recipe';
import { ingredientConversionService } from '@/services/recipe/ingredientConversionService';
import { recipeTagService } from '@/services/recipe/recipeTagService';
import { useQuickRecipeFormState } from './useQuickRecipeFormState';
import { RECIPES } from '@/constants/queryKeys';

/**
 * Main hook for quick recipe form functionality
 */
export const useQuickRecipeForm = (
  recipe?: Recipe,
  onRecipeCreated?: () => void,
  onRecipeUpdated?: () => void
) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    formData,
    setFormData,
    ingredients,
    setIngredients,
    isSubmitting,
    setIsSubmitting,
    successData,
    setSuccessData,
    handleDialogClose
  } = useQuickRecipeFormState(recipe);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('Please enter a recipe title');
      return;
    }

    const validIngredients = ingredients.filter(ing => ing.name.trim());
    if (validIngredients.length === 0) {
      toast.error('Please add at least one ingredient');
      return;
    }

    try {
      setIsSubmitting(true);

      const ingredientsList = validIngredients.map(ing => `${ing.quantity || ''} ${ing.unit || ''} ${ing.name}`.trim());
      
      // Convert ingredients using OpenAI
      const conversionResult = await ingredientConversionService.convertIngredients(validIngredients);
      
      // Use converted ingredients or fallback to original
      const metricIngredients = conversionResult?.structured_ingredients_metric || validIngredients;
      const imperialIngredients = conversionResult?.structured_ingredients_imperial || validIngredients;
      
      if (recipe) {
        // Update existing recipe
        const updatedData = {
          title: formData.title.trim(),
          description: `Quick recipe for ${formData.title}`,
          ingredients: ingredientsList,
          instructions: formData.instructions ? [formData.instructions] : ['Prepare and cook ingredients as desired'],
          servings: formData.servings,
          quickIngredients: validIngredients,
          structured_ingredients_metric: metricIngredients,
          structured_ingredients_imperial: imperialIngredients,
          user_image_url: formData.userImageUrl || null,
          user_image_path: formData.userImagePath || null,
          image_url: formData.imageUrl || null,
          updatedAt: new Date().toISOString()
        };

        await recipeService.updateRecipe(recipe.id, updatedData);
        
        // Set success data for dialog
        setSuccessData({
          id: recipe.id,
          title: formData.title.trim(),
          isEditing: true
        });
        
        // Generate tags for updated recipe
        recipeTagService.generateTagsForRecipe(
          recipe.id,
          formData.title.trim(),
          ingredientsList,
          formData.instructions || 'Prepare and cook ingredients as desired'
        );
        
        // Invalidate recipes cache to refresh collection
        queryClient.invalidateQueries({ queryKey: [RECIPES] });
        
        if (onRecipeUpdated) {
          onRecipeUpdated();
        }
      } else {
        // Create new recipe
        const quickRecipe = {
          id: crypto.randomUUID(),
          title: formData.title.trim(),
          description: `Quick recipe for ${formData.title}`,
          ingredients: ingredientsList,
          instructions: formData.instructions ? [formData.instructions] : ['Prepare and cook ingredients as desired'],
          servings: formData.servings,
          tags: [],
          isQuickRecipe: true,
          quickIngredients: validIngredients,
          prepTime: null,
          cookTime: null,
          totalTime: null,
          source_url: null,
          user_image_url: formData.userImageUrl || null,
          user_image_path: formData.userImagePath || null,
          image_url: formData.imageUrl || null,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          user_id: null,
          imperial_version: null,
          metric_version: null,
          structured_ingredients_metric: metricIngredients,
          structured_ingredients_imperial: imperialIngredients,
          baseMeasurementSystem: 'metric',
          suggestion_id: null
        };

        const savedRecipe = await recipeService.saveRecipe(quickRecipe);
        
        if (savedRecipe) {
          // Set success data for dialog
          setSuccessData({
            id: savedRecipe.id,
            title: formData.title.trim(),
            isEditing: false
          });
          
          recipeTagService.generateTagsForRecipe(
            savedRecipe.id,
            formData.title.trim(),
            ingredientsList,
            formData.instructions || 'Prepare and cook ingredients as desired'
          );
          
          // Invalidate recipes cache to refresh collection
          queryClient.invalidateQueries({ queryKey: [RECIPES] });
          
          if (onRecipeCreated) {
            onRecipeCreated();
          }
          
          // Reset form for new recipe creation
          setFormData({
            title: '',
            servings: '4',
            instructions: '',
            imageUrl: '',
            userImageUrl: '',
            userImagePath: ''
          });
          setIngredients([{ name: '', quantity: null, unit: '' }]);
        }
      }
    } catch (error) {
      console.error('Error saving quick recipe:', error);
      toast.error(`Failed to ${recipe ? 'update' : 'create'} quick recipe`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    formData,
    setFormData,
    ingredients,
    setIngredients,
    isSubmitting,
    handleSubmit,
    successData,
    handleDialogClose
  };
};
